import { useEffect, useState } from "react";
import { Check, Loader2, Zap, Send, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { dayLabel } from "./portal";

const MODES = ["Remote", "Hybrid", "On-site"];

export function RulesCard({ cfg, save, run, running }) {
  const [fit, setFit] = useState(cfg.min_fit);
  useEffect(() => setFit(cfg.min_fit), [cfg.min_fit]);
  const caps = [...new Set([1, 3, 5, 10, cfg.max_total])].sort((a, b) => a - b);
  const modes = cfg.work_modes || [];
  return (
    <section className="hs-card p-5 sm:p-6 space-y-6" data-testid="autoapply-rules">
      <h2 className="text-[15px] font-semibold">Rules</h2>
      <div className="space-y-3">
        <div className="flex justify-between text-sm"><span>Minimum fit</span><span className="hs-mono font-semibold" data-testid="autoapply-minfit-value">{fit}%</span></div>
        <Slider value={[fit]} min={40} max={95} step={5} onValueChange={(v) => setFit(v[0])} onValueCommit={(v) => save({ min_fit: v[0] })} aria-label="Minimum fit" data-testid="autoapply-minfit" />
      </div>
      <div className="space-y-2.5">
        <div className="text-sm">Application cap</div>
        <div role="radiogroup" aria-label="Application cap" className="flex rounded-xl border border-border p-1 gap-1" data-testid="autoapply-maxtotal">
          {caps.map((n) => (
            <button key={n} role="radio" aria-checked={cfg.max_total === n} onClick={() => save({ max_total: n })} data-testid={`autoapply-cap-${n}`}
              className={cn("flex-1 h-9 rounded-lg text-sm tabular-nums transition-colors", cfg.max_total === n ? "bg-foreground/[0.09] font-semibold" : "text-muted-foreground hover:text-foreground")}>{n}</button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">The agent stops after this many applications in total.</p>
      </div>
      <div className="space-y-2.5">
        <div className="text-sm">Preferred work modes</div>
        <div className="flex flex-wrap gap-2">
          {MODES.map((m) => {
            const on = modes.includes(m);
            return (
              <button key={m} aria-pressed={on} onClick={() => save({ work_modes: on ? modes.filter((x) => x !== m) : [...modes, m] })} data-testid={`mode-${m.toLowerCase()}`}
                className={cn("hs-chip", on && "hs-chip-active")}>{on && <Check className="h-3 w-3" />}{m}</button>
            );
          })}
        </div>
      </div>
      <div className="space-y-2">
        <Button onClick={run} disabled={running || !cfg.enabled} className="w-full gap-1.5" data-testid="autoapply-run">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />} Run agent now
        </Button>
        {!cfg.enabled && <p className="text-xs text-muted-foreground text-center">Turn the agent on to run it.</p>}
      </div>
    </section>
  );
}

export function ActivityCard({ log, cap, used }) {
  return (
    <section className="hs-card overflow-hidden" data-testid="autoapply-activity">
      <header className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-border">
        <h2 className="text-[15px] font-semibold">Activity</h2>
        <span className="text-sm text-muted-foreground" data-testid="autoapply-used">{used} of {cap} used</span>
      </header>
      {log.length === 0 ? (
        <p className="px-6 py-12 text-center text-sm text-muted-foreground" data-testid="autoapply-empty">No activity yet. Turn the agent on and run it — every application and skip is logged here with a reason.</p>
      ) : (
        <ul className="divide-y divide-border max-h-[560px] overflow-y-auto">
          {log.slice(0, 50).map((e, i) => {
            const ok = e.action === "applied";
            return (
              <li key={i} className="grid grid-cols-[56px_auto_minmax(0,1fr)_auto] items-center gap-3 px-5 sm:px-6 py-3.5" data-testid={`log-${i}`}>
                <span className="hs-mono text-xs text-muted-foreground">{dayLabel(e.timestamp)}</span>
                <span className={cn("h-8 w-8 rounded-lg flex items-center justify-center", ok ? "bg-primary/12 text-primary" : "bg-foreground/[0.06] text-muted-foreground")}>{ok ? <Send className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}</span>
                <span className="text-sm min-w-0">{ok ? `Applied to ${e.title} (${e.fit}%)` : `Skipped ${e.title}: ${e.reason}`}</span>
                <span className={cn("text-sm", ok ? "text-primary" : "text-muted-foreground")}>{ok ? "Applied" : "Skipped"}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
